<template>
  <div class="step-content">
    <h3>Impacted Locations & Recipients</h3>
    <p class="step-description">
      Select locations and review their contacts. Click the contact count to view details in a modal.
    </p>

    <div class="summary-bar">
      <div class="summary-item">
        <i class="fa fa-map-marker"></i>
        <span class="summary-label">Locations:</span>
        <span class="summary-value">{{ selectedSitesCount }} of {{ sites.length }}</span>
      </div>
      <div class="summary-item">
        <i class="fa fa-users"></i>
        <span class="summary-label">Total Recipients:</span>
        <span class="summary-value">{{ totalRecipients }}</span>
      </div>
      <div class="summary-item" v-if="messageTypeName">
        <i class="fa fa-envelope"></i>
        <span class="summary-label">Message Type:</span>
        <span class="summary-value">{{ messageTypeName }}</span>
      </div>
      <div class="summary-item" v-if="priority">
        <i class="fa fa-flag"></i>
        <span class="summary-label">Priority:</span>
        <span class="summary-value">{{ priority.charAt(0).toUpperCase() + priority.slice(1) }}</span>
      </div>
      <div class="summary-item" v-if="responseOption">
        <i class="fa fa-reply"></i>
        <span class="summary-label">Response:</span>
        <span class="summary-value">{{ responseOption === 'ok_not_ok' ? 'OK/Not OK' : 'None' }}</span>
      </div>
    </div>

    <div
      v-if="selectedSitesCount > 0 && totalRecipients === 0"
      class="no-recipients-warning"
    >
      <i class="fa fa-exclamation-triangle"></i>
      No contacts found for the selected locations. Please select locations with contacts to proceed.
    </div>

    <div v-if="sites && sites.length > 0" class="locations-table-container">
      <!-- Locations Table -->
      <div class="table-wrapper">
        <table class="locations-table">
          <thead>
            <tr>
              <th class="checkbox-column">
                <input
                  type="checkbox"
                  :checked="isAllSelected"
                  :indeterminate.prop="isIndeterminate"
                  @change="$emit('toggle-all')"
                />
              </th>
              <th class="location-name-column">Location Name</th>
              <th class="contacts-count-column">Contacts</th>
            </tr>
          </thead>
          <tbody>
            <!-- Location Row -->
            <tr
              v-for="site in paginatedSites"
              :key="`location-${site.key}`"
              class="location-row"
              :class="{ 'selected': isLocationSelected(site.key) }"
            >
              <td class="checkbox-cell">
                <input
                  type="checkbox"
                  :checked="isLocationSelected(site.key)"
                  @change="$emit('toggle-site', site.key)"
                />
              </td>
              <td class="location-name-cell">
                <strong>{{ site.site.name || site.site.id || site.name || 'Unknown Location' }}</strong>
              </td>
              <td class="contacts-count-cell">
                <button
                  v-if="getTotalLocationContactCount(site.key) > 0"
                  type="button"
                  class="btn-contacts-count"
                  @click="openContactsModal(site)"
                  :class="{ 'has-removed': getLocationContactCount(site.key) < getTotalLocationContactCount(site.key) }"
                  :disabled="!isLocationSelected(site.key)"
                >
                  <i class="fa fa-users"></i>
                  {{ getLocationContactCount(site.key) }}/{{ getTotalLocationContactCount(site.key) }}
                </button>
                <span v-else class="no-contacts">0</span>
              </td>
            </tr>

            <tr v-if="paginatedSites.length === 0" class="empty-row">
              <td colspan="3" class="text-center">
                <i class="fa fa-info-circle"></i>
                No locations available
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div class="pagination-container" v-if="totalPages > 1">
        <div class="pagination-info">
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="items-info">({{ sites.length }} locations total)</span>
        </div>

        <div class="pagination-controls">
          <button
            class="btn btn-sm btn-outline-secondary pagination-btn"
            @click="$emit('go-to-previous-page')"
            :disabled="currentPage <= 1"
          >
            <i class="fa fa-chevron-left"></i>
            Previous
          </button>

          <div class="page-numbers">
            <button
              v-for="page in displayedPages"
              :key="page"
              class="btn btn-sm page-number-btn"
              :class="{
                'btn-primary': page === currentPage,
                'btn-outline-secondary': page !== currentPage,
              }"
              @click="$emit('go-to-page', page)"
            >
              {{ page }}
            </button>
          </div>

          <button
            class="btn btn-sm btn-outline-secondary pagination-btn"
            @click="$emit('go-to-next-page')"
            :disabled="currentPage >= totalPages"
          >
            Next
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-else class="no-sites-message">
      <i class="fa fa-info-circle"></i>
      <p>No locations available for selection.</p>
    </div>

    <!-- Contacts Modal (using Teleport to render outside parent modal) -->
    <Teleport to="#modal-destination" v-if="showContactsModal">
      <div class="locations-contacts-modal-overlay" @click="handleModalOverlayClick">
        <div class="locations-contacts-modal-container" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fa fa-users"></i>
            Contacts for {{ selectedSite ? (selectedSite.site.name || selectedSite.site.id || selectedSite.name || 'Unknown Location') : '' }}
          </h4>
          <button type="button" class="close-button" @click="closeContactsModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="contacts-filters-modal">
            <input
              type="text"
              v-model="modalContactsFilter"
              placeholder="Filter contacts by name or email..."
              class="filter-input-modal"
            />
          </div>

          <div class="contacts-table-wrapper">
            <table class="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Cell</th>
                  <th>Language</th>
                  <th>Role(s)</th>
                  <th>Contact Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-if="allModalContacts.length === 0"
                  class="empty-row"
                >
                  <td colspan="7" class="text-center">
                    {{ modalContactsFilter ? 'No contacts match your filter' : 'No contacts found for this location' }}
                  </td>
                </tr>
                <tr
                  v-else
                  v-for="contact in allModalContacts"
                  :key="contact.id"
                  class="contact-row"
                  :class="{ 'deleted-row': isContactDeleted(contact.id, contact.locationId) }"
                >
                  <td class="name-cell">
                    {{ contact.name || 'N/A' }}
                    <span v-if="isContactDeleted(contact.id, contact.locationId)" class="deleted-badge">Removed</span>
                  </td>
                  <td class="email-cell">{{ contact.email || 'N/A' }}</td>
                  <td class="phone-cell">{{ contact.cell || 'N/A' }}</td>
                  <td class="language-cell">{{ contact.preferredLanguageCode || 'N/A' }}</td>
                  <td class="role-cell">
                    <span v-if="contact.locationRoles && contact.locationRoles.length > 0">
                      {{ getContactRoles(contact) }}
                    </span>
                    <span v-else>N/A</span>
                  </td>
                  <td class="contact-type-cell">
                    <span v-if="contact.locationRoles && contact.locationRoles.length > 0">
                      {{ getContactTypes(contact) }}
                    </span>
                    <span v-else>N/A</span>
                  </td>
                  <td class="actions-cell">
                    <button
                      v-if="!isContactDeleted(contact.id, contact.locationId)"
                      type="button"
                      class="btn-remove-contact"
                      @click="removeContactFromModal(contact.id, contact.locationId)"
                      title="Remove contact from list"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                    <button
                      v-else
                      type="button"
                      class="btn-restore-contact"
                      @click="restoreContactFromModal(contact.id, contact.locationId)"
                      title="Restore contact to list"
                    >
                      <i class="fa fa-undo"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeContactsModal">
            Close
          </button>
        </div>
        </div>
      </div>
    </Teleport>
  </div>
</template><script>
export default {
  name: "LocationsWithContactsStep",
  props: {
    // Location props
    sites: { type: Array, required: true },
    selectedSiteKeys: { type: Array, required: true },
    selectedSitesCount: { type: Number, required: true },
    paginatedSites: { type: Array, required: true },
    isAllSelected: { type: Boolean, required: true },
    isIndeterminate: { type: Boolean, required: true },
    currentPage: { type: Number, required: true },
    totalPages: { type: Number, required: true },

    // Contact props
    contactsBySite: { type: Object, required: true }, // Object keyed by site.key with array of contacts
    totalContactsBySite: { type: Object, required: true }, // Object keyed by site.key with total count (including deleted)
    deletedContacts: { type: Array, required: true },
    deletedContactsBySite: { type: Object, required: true }, // Object keyed by site.key with array of deleted contacts
    showingDeletedContacts: { type: Boolean, required: true },
    contactsFilter: { type: String, default: '' },

    // Message props for summary bar
    messageTypeCode: { type: String, default: '' },
    messageTypeName: { type: String, default: '' },
    priority: { type: String, default: '' },
    responseOption: { type: String, default: '' },
  },
  data() {
    return {
      expandedLocations: new Set(), // Track which locations are expanded (legacy, not used anymore)
      showContactsModal: false,
      selectedSite: null,
      modalContactsFilter: '',
    };
  },
  computed: {
    totalRecipients() {
      let total = 0;
      // Only count contacts from selected sites
      Object.keys(this.contactsBySite).forEach(siteKey => {
        if (this.selectedSiteKeys.includes(siteKey)) {
          total += this.contactsBySite[siteKey].length;
        }
      });
      // contactsBySite already excludes deleted contacts (filtered in parent component)
      return total;
    },
    displayedPages() {
      const pages = [];
      for (let i = 1; i <= this.totalPages; i++) {
        if (
          Math.abs(i - this.currentPage) <= 2 ||
          i === 1 ||
          i === this.totalPages
        ) {
          pages.push(i);
        }
      }
      return pages;
    },
    filteredModalContacts() {
      if (!this.selectedSite) return [];
      
      const contacts = this.contactsBySite[this.selectedSite.key] || [];
      
      if (!this.modalContactsFilter) {
        return contacts;
      }
      
      const filterLower = this.modalContactsFilter.toLowerCase();
      return contacts.filter(contact => {
        return (
          (contact.name && contact.name.toLowerCase().includes(filterLower)) ||
          (contact.email && contact.email.toLowerCase().includes(filterLower))
        );
      });
    },
    // All contacts for modal (active + deleted for this site)
    allModalContacts() {
      if (!this.selectedSite) return [];
      
      const siteKey = this.selectedSite.key;
      
      // Get active contacts for this site
      const activeContacts = this.contactsBySite[siteKey] || [];
      
      // Get deleted contacts for this site from the grouped object
      const deletedContactsForSite = this.deletedContactsBySite[siteKey] || [];
      
      // Combine both arrays
      const allContacts = [...activeContacts, ...deletedContactsForSite];
      
      // Apply filter if exists
      if (!this.modalContactsFilter) {
        return allContacts;
      }
      
      const filterLower = this.modalContactsFilter.toLowerCase();
      return allContacts.filter(contact => {
        return (
          (contact.name && contact.name.toLowerCase().includes(filterLower)) ||
          (contact.email && contact.email.toLowerCase().includes(filterLower))
        );
      });
    },
  },
  methods: {
    isLocationSelected(siteKey) {
      return this.selectedSiteKeys.includes(siteKey);
    },
    isLocationExpanded(siteKey) {
      return this.expandedLocations.has(siteKey);
    },
    toggleLocationExpansion(siteKey) {
      if (this.expandedLocations.has(siteKey)) {
        this.expandedLocations.delete(siteKey);
      } else {
        this.expandedLocations.add(siteKey);
      }
      // Force reactivity update
      this.$forceUpdate();
    },
    openContactsModal(site) {
      this.selectedSite = site;
      this.modalContactsFilter = '';
      this.showContactsModal = true;
    },
    closeContactsModal() {
      this.showContactsModal = false;
      this.selectedSite = null;
      this.modalContactsFilter = '';
    },
    handleModalOverlayClick() {
      this.closeContactsModal();
    },
    removeContactFromModal(contactId, locationId) {
      this.$emit('remove-contact', contactId, locationId);
    },
    restoreContactFromModal(contactId, locationId) {
      this.$emit('restore-contact', contactId, locationId);
    },
    isContactDeleted(contactId, locationId) {
      return this.deletedContacts.some(contact => 
        contact.id === contactId && contact.locationId === locationId
      );
    },
    getLocationContactCount(siteKey) {
      const contacts = this.contactsBySite[siteKey] || [];
      return contacts.length;
    },
    getTotalLocationContactCount(siteKey) {
      return this.totalContactsBySite[siteKey] || 0;
    },
    getLocationContacts(siteKey) {
      const contacts = this.contactsBySite[siteKey] || [];
      if (!this.contactsFilter) {
        return contacts;
      }
      const filterLower = this.contactsFilter.toLowerCase();
      return contacts.filter(contact => {
        return (
          (contact.name && contact.name.toLowerCase().includes(filterLower)) ||
          (contact.email && contact.email.toLowerCase().includes(filterLower))
        );
      });
    },
    getContactRoles(contact) {
      if (!contact.locationRoles || contact.locationRoles.length === 0) {
        return 'N/A';
      }
      const roleNames = contact.locationRoles
        .map(lr => lr.roleName)
        .filter(name => name);
      const uniqueRoles = [...new Set(roleNames)];
      return uniqueRoles.length > 0 ? uniqueRoles.join(', ') : 'N/A';
    },
    getContactTypes(contact) {
      if (!contact.locationRoles || contact.locationRoles.length === 0) {
        return 'N/A';
      }
      const contactTypes = contact.locationRoles
        .map(lr => lr.contactTypeDescription || lr.contactTypeCode)
        .filter(type => type);
      const uniqueTypes = [...new Set(contactTypes)];
      return uniqueTypes.length > 0 ? uniqueTypes.join(', ') : 'N/A';
    },
  },
};
</script>

<style scoped>
.step-description {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 16px;
}

.summary-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 20px;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.summary-item i {
  font-size: 18px;
}

.summary-label {
  font-weight: 500;
  opacity: 0.9;
}

.summary-value {
  font-weight: 700;
  font-size: 16px;
}

.deleted-count {
  color: #ffeb3b;
  font-weight: 600;
  margin-left: 4px;
  font-size: 12px;
}

.summary-actions {
  display: flex;
  gap: 8px;
}

.no-recipients-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-left: 4px solid #ffc107;
  border-radius: 6px;
  color: #856404;
  font-size: 14px;
  font-weight: 500;
}

.no-recipients-warning i {
  font-size: 16px;
  flex-shrink: 0;
}

.locations-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
}

.locations-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.locations-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
}

.locations-table th {
  padding: 12px 10px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #e0e0e0;
  white-space: nowrap;
}

.checkbox-column { width: 50px; text-align: center; }
.location-name-column { width: auto; min-width: 200px; }
.contacts-count-column { width: 120px; text-align: center; }

.location-row {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.location-row:hover {
  background-color: #f8f9fa;
}

.location-row.selected {
  background-color: #e3f2fd;
}

.location-row.selected:hover {
  background-color: #bbdefb;
}

.locations-table td {
  padding: 12px 10px;
  vertical-align: middle;
}

.checkbox-cell {
  text-align: center;
}

.checkbox-cell input[type="checkbox"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
}

.expand-cell {
  text-align: center;
}

.btn-expand {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  color: #007bff;
  font-size: 14px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-expand:hover {
  background-color: #007bff;
  color: white;
}

.location-name-cell strong {
  color: #2c3e50;
  font-size: 14px;
}

.badge {
  font-size: 11px;
  padding: 4px 8px;
  font-weight: 500;
  border-radius: 4px;
}

.badge-risk-high {
  background-color: #dc3545;
  color: white;
}

.badge-risk-medium {
  background-color: #ffc107;
  color: #212529;
}

.badge-risk-low {
  background-color: #28a745;
  color: white;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.contacts-count-cell {
  text-align: center;
}

.btn-contacts-count {
  background: #007bff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-contacts-count:disabled {
  background: #6c757d;
  color: #dee2e6;
  cursor: not-allowed;
  opacity: 0.5;
  transform: none;
}

.btn-contacts-count:hover:not(:disabled) {
  background: #0056b3;
  transform: scale(1.05);
}

.btn-contacts-count.expanded {
  background: #28a745;
}

.btn-contacts-count.expanded:hover:not(:disabled) {
  background: #1e7e34;
}

.btn-contacts-count.has-removed {
  background: #ffc107;
  color: #212529;
}

.btn-contacts-count.has-removed:hover:not(:disabled) {
  background: #e0a800;
  color: #212529;
}

.no-contacts {
  color: #9aa0a6;
  font-size: 13px;
}

.contacts-header-row {
  background-color: #f8f9fa;
}

.contacts-section {
  padding: 16px !important;
}

.contacts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 2px solid #007bff;
}

.contacts-header h5 {
  margin: 0;
  color: #2c3e50;
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.contacts-filters {
  flex: 1;
  max-width: 400px;
  margin-left: 16px;
}

.filter-input-inline {
  width: 100%;
  padding: 6px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.filter-input-inline:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.contacts-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
}

.contacts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.contacts-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
}

.contacts-table th {
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  font-size: 12px;
  white-space: nowrap;
}

.contacts-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.contacts-table tbody tr:hover {
  background-color: #f8f9fa;
}

.contact-row td {
  padding: 10px 8px;
  vertical-align: middle;
  font-size: 13px;
}

.name-cell { width: 18%; }
.email-cell { width: 22%; }
.phone-cell { width: 14%; }
.language-cell { width: 8%; text-align: center; }
.role-cell { width: 20%; }
.contact-type-cell { width: 12%; }
.actions-cell { width: 6%; text-align: center; }

.btn-remove-contact,
.btn-restore-contact {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 13px;
}

.btn-remove-contact {
  color: #dc3545;
}

.btn-remove-contact:hover {
  background-color: #dc3545;
  color: white;
  transform: scale(1.1);
}

.btn-restore-contact {
  color: #28a745;
}

.btn-restore-contact:hover {
  background-color: #28a745;
  color: white;
  transform: scale(1.1);
}

.empty-row {
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.empty-row td {
  padding: 20px;
}

.no-sites-message {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.no-sites-message i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #dee2e6;
}

.no-sites-message p {
  margin: 0;
  font-size: 16px;
}

.pagination-container {
  padding: 16px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.pagination-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: #6c757d;
}

.items-info {
  font-size: 12px;
  color: #9aa0a6;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 8px;
}

.page-number-btn {
  min-width: 32px;
  height: 32px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.deleted-row {
  background-color: #fffbf0 !important;
  opacity: 0.7;
}

.deleted-row td {
  text-decoration: line-through;
  color: #999;
}

.deleted-row:hover {
  background-color: #fff3cd !important;
  opacity: 0.85;
}

.deleted-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background-color: #ffc107;
  color: #212529;
  font-size: 11px;
  font-weight: 600;
  border-radius: 12px;
  text-transform: uppercase;
}

@media (max-width: 768px) {
  .summary-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .contacts-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .contacts-filters {
    margin-left: 0;
    margin-top: 12px;
    max-width: 100%;
  }

  .pagination-info {
    flex-direction: column;
    gap: 4px;
    text-align: center;
  }

  .pagination-controls {
    flex-wrap: wrap;
    gap: 4px;
  }

  .page-numbers {
    margin: 0 4px;
  }
}

/* Modal Styles */
.locations-contacts-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  backdrop-filter: blur(3px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.locations-contacts-modal-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 1000px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
  position: relative;
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 2px solid #007bff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-title {
  margin: 0;
  color: #2c3e50;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-title i {
  color: #007bff;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  line-height: 1;
}

.close-button:hover {
  background-color: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.contacts-filters-modal {
  margin-bottom: 16px;
}

.filter-input-modal {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #ced4da;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.filter-input-modal:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.15);
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
}

.modal-footer .btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.modal-footer .btn-secondary {
  background: #6c757d;
  color: white;
  border: none;
}

.modal-footer .btn-secondary:hover {
  background: #5a6268;
}
</style>
